import { useTranslation } from "react-i18next";
import { useState } from "react";
import { format } from "date-fns";
import { useFormContext, useWatch } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { useQuotationPriceUpdate } from "@/hooks/useQuotationPriceUpdate";
import { formatPrice } from "@/lib/money.util";
import { ConfirmationModalProps } from "@/types/confirmationModal";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { QuotationUpdateBoFormData } from "@/types/forms/bo/expeditionBo.form";

export const PriceUpdate = ({ expedition }: { expedition: GetOneExpeditionBoResponseDto }) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useFormContext<QuotationUpdateBoFormData>();
  const updatePriceParameters = useWatch({
    control: methods.control,
    name: "updatePriceParameters",
  });
  const [confirmationModalState, setConfirmationModalState] =
    useState<ConfirmationModalProps | null>(null);
  const { isReady, hasPriceChanged, currentTotal, newTotal, changes } =
    useQuotationPriceUpdate(expedition);

  if (!isReady) return null;

  return (
    <div className="flex w-full flex-col items-center justify-between gap-2 rounded-lg bg-[#3562F8]/10 p-4 @3xl:flex-row">
      <div className="flex w-full flex-col gap-2">
        <p className="text-base font-bold text-blue-600">{t("priceUpdate.title")}</p>
        {hasPriceChanged ? (
          <ul className="list-disc pl-5 text-base text-blue-600">
            {changes.map((c) => (
              <li key={c.reason}>
                {c.message}
                {c.updatedAt && ` (${format(c.updatedAt, "dd/MM/yyyy")})`}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-base text-blue-600">{t("priceUpdate.noChange")}</p>
        )}
      </div>
      {hasPriceChanged && (
        <div className="flex flex-col items-center justify-between gap-4 self-center rounded-lg bg-blue-600 p-2 @3xl:flex-row @3xl:self-center">
          <div className="flex w-full flex-col gap-1">
            <p className="text-base text-white @3xl:text-nowrap">
              {t("priceUpdate.referencePrice", { price: formatPrice(currentTotal) })}
            </p>
            <p className="text-base font-bold text-white @3xl:text-nowrap">
              {t("priceUpdate.newPrice", { price: formatPrice(newTotal) })}
            </p>
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={() =>
              !updatePriceParameters
                ? setConfirmationModalState({
                    title: t("priceUpdate.updateConfirmTitle"),
                    text: t("priceUpdate.updateConfirmDescription"),
                    confirm: () => {
                      methods.setValue("updatePriceParameters", true);
                    },
                  })
                : setConfirmationModalState({
                    title: t("priceUpdate.cancelConfirmTitle"),
                    text: t("priceUpdate.cancelConfirmDescription"),
                    confirm: () => {
                      methods.setValue("updatePriceParameters", false);
                    },
                  })
            }
          >
            {updatePriceParameters ? t("priceUpdate.cancelUpdate") : t("priceUpdate.update")}
          </Button>
        </div>
      )}
      {confirmationModalState && (
        <ConfirmationModal
          open={true}
          onClose={() => setConfirmationModalState(null)}
          title={confirmationModalState.title}
          text={confirmationModalState.text}
          confirm={confirmationModalState.confirm}
        />
      )}
    </div>
  );
};
